// Libs
import classNames from 'classnames/bind';
import moment from 'moment';
import { TFunction } from 'i18next';
import { useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { Box, Stack } from '@mui/material';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { ChangeEvent, useCallback, useEffect, useMemo, useState } from 'react';
// Components, Layouts, Pages
import {
  BaseDatePicker,
  BaseDynamicSelect,
  BaseFilters,
  BaseInputSearch,
  BaseMoreAction,
  BaseSelect,
  BaseStatus,
  BaseTable,
  BaseToolbar,
  ConfirmModal,
  Loading,
  SortComponent,
  UnderDevelopment,
} from '~/components/common';
// Others
import useDebounce from '~/utils/hooks/useDebounce';
import {
  DEFAULT_CURRENT_PAGE,
  DEFAULT_DEBOUNCE_DELAY_TIME,
  DEFAULT_LIMIT_PAGE,
  DEFAULT_SELECT_DYNAMIC_RESULT_FETCH_DATA,
  DEFAULT_SORT_ORDER,
  EMPTY_STRING,
  EMPTY_VALUE,
  FILTER_DEFAULT_VALUES,
  optionStatusOrder,
} from '~/utils/constants/common';
import { IOrder, IParamQueryOrder } from '~/utils/interface/order';
import { StorageEnum } from '~/utils/enum/base';
import { useAppDispatch } from '~/utils/redux/hooks';
import { adminRouteAbsolute } from '~/utils/routes/roleRoutes/admin';
import { IPaginationResponse } from '~/utils/interface/base';
import {
  DateFormatEnum,
  OrderListTypeEnum,
  OrderPickupEnum,
  OrderStatusEnum,
  RoleEnum,
  TypeCrmEnum,
} from '~/utils/enum/common';
import { deleteOrder, getListOrder } from '~/thunks/order/orderThunk';
import { orderActions, selectIsRefreshOrderList } from '~/thunks/order/orderSlice';
import { formatCurrencyUSD, formatDate, getFullName } from '~/utils/helper/common';
import {
  IBaseMoreAction,
  IBaseOption,
  IFilterValueChange,
  IHeaderCellType,
  IPayloadCallApiFn,
  IResultCallApiFn,
  ISortBy,
} from '~/utils/interface/common';
import { crewRouteAbsolute } from '~/utils/routes/roleRoutes/crew';
import { loaderRouteAbsolute } from '~/utils/routes/roleRoutes/loader';
import { foremanRouteAbsolute } from '~/utils/routes/roleRoutes/foreman';
import { salesAdminRouteAbsolute } from '~/utils/routes/roleRoutes/salesAdmin';
import { salesPersonRouteAbsolute } from '~/utils/routes/roleRoutes/salesPerson';
import { warehouseLeadRouteAbsolute } from '~/utils/routes/roleRoutes/warehouseLead';
import { warehouseStaffRouteAbsolute } from '~/utils/routes/roleRoutes/warehouseStaff';
import { operationsManagerRouteAbsolute } from '~/utils/routes/roleRoutes/operationsManager';
import { ORDER_PERMISSIONS } from '~/utils/constants/permissions';
import { IClient, IOptionClient, IQueryParamClient } from '~/utils/interface/client';
import { IQueryParamUser } from '~/utils/interface/user';
import { usePermissions } from '~/utils/hooks/userPermissions';
import { getListClient } from '~/thunks/client/clientThunk';
import { getListUser } from '~/thunks/user/userThunk';
// Styles, images, icons
import styles from './Order.module.scss';
import { icons } from '~/assets';

type Props = {};

const cx = classNames.bind(styles);

const columnsOrder = (
  t: TFunction,
  sortBy: ISortBy[],
  handleSort: (sortType: string, name: string) => void,
  onView: (id: string, item: IOrder) => void,
  onEdit: (id: string) => void,
  onDelete: (id: string) => void,
  isDetail: boolean,
  isEdit: boolean,
  isDelete: boolean
): IHeaderCellType<IOrder>[] => {
  const getSortType = (name: string) => sortBy.find((item) => item.name === name)?.typeSort || EMPTY_STRING;

  return [
    {
      id: 'orderNumber',
      label: (
        <Stack direction={'row'} alignItems={'center'} gap={2}>
          <Box>{t('table_header_title_order_number')}</Box>
          <SortComponent name='order number' sortType={getSortType('order number')} handleSort={handleSort} />
        </Stack>
      ),
      render: (record: IOrder) => record?.orderNumber || EMPTY_VALUE,
    },
    {
      id: 'eventTitle',
      label: (
        <Stack direction={'row'} alignItems={'center'} gap={2}>
          <Box>{t('table_header_title_event_name')}</Box>
          <SortComponent name='event name' sortType={getSortType('event name')} handleSort={handleSort} />
        </Stack>
      ),
      render: (record: IOrder) => record?.evenTitle || EMPTY_VALUE,
    },
    {
      id: 'client',
      label: (
        <Stack direction={'row'} alignItems={'center'} gap={2}>
          <Box>{t('table_header_title_client_name')}</Box>
          <SortComponent name='client name' sortType={getSortType('client name')} handleSort={handleSort} />
        </Stack>
      ),
      render: (record: IOrder) =>
        getFullName({ firstName: record?.client?.firstName, lastName: record?.client?.lastName }) || EMPTY_VALUE,
    },
    {
      id: 'date',
      label: (
        <Stack direction={'row'} alignItems={'center'} gap={2}>
          <Box>{t('table_header_title_event_date')}</Box>
          <SortComponent name='start date' sortType={getSortType('start date')} handleSort={handleSort} />
        </Stack>
      ),
      render: (record: IOrder) => formatDate(record?.startDate, DateFormatEnum.MM_DD_YYYY) || EMPTY_VALUE,
    },
    {
      id: 'address',
      label: (
        <Stack direction={'row'} alignItems={'center'} gap={2}>
          <Box>{t('table_header_title_event_address')}</Box>
          <SortComponent name='pickup address' sortType={getSortType('pickup address')} handleSort={handleSort} />
        </Stack>
      ),
      render: (record: IOrder) => record?.pickupAddress?.address || EMPTY_VALUE,
    },
    {
      id: 'total',
      label: (
        <Stack direction={'row'} alignItems={'center'} gap={2}>
          <Box>{t('table_header_title_order_total')}</Box>
          <SortComponent name='total' sortType={getSortType('total')} handleSort={handleSort} />
        </Stack>
      ),
      render: (record: IOrder) => formatCurrencyUSD({ value: record?.total, showDecimal: true }),
    },
    {
      id: 'status',
      label: (
        <Stack direction={'row'} alignItems={'center'} gap={2}>
          <Box>{t('table_header_title_status')}</Box>
          <SortComponent name='status' sortType={getSortType('status')} handleSort={handleSort} />
        </Stack>
      ),
      render: (record: IOrder) => <BaseStatus label={record?.status || EMPTY_STRING} />,
    },
    {
      id: 'action',
      label: t('table_header_title_actions'),
      minWidth: 100,
      align: 'center',
      render: (record: IOrder) => {
        const actions: IBaseMoreAction[] = [
          ...(isDetail
            ? [
                {
                  label: t('table_action_view', { ns: 'common/base' }),
                  icon: (
                    <img
                      src={icons.tableIconActionView}
                      alt={t('common_alt_img', { ns: 'common/base' })}
                      width={16}
                      height={16}
                    />
                  ),
                  onClick: () => onView(record.id, record),
                },
              ]
            : []),
          ...(isEdit && record?.status !== OrderStatusEnum.FINALIZED
            ? [
                {
                  label: t('table_action_edit', { ns: 'common/base' }),
                  icon: (
                    <img
                      src={icons.tableIconActionEdit}
                      alt={t('common_alt_img', { ns: 'common/base' })}
                      width={16}
                      height={16}
                    />
                  ),
                  onClick: () => onEdit(record.id),
                },
              ]
            : []),
          ...(isDelete
            ? [
                {
                  label: t('table_action_delete', { ns: 'common/base' }),
                  icon: (
                    <img
                      src={icons.tableIconActionDelete}
                      alt={t('common_alt_img', { ns: 'common/base' })}
                      width={16}
                      height={16}
                    />
                  ),
                  hasOtherColor: true,
                  onClick: () => onDelete(record.id),
                },
              ]
            : []),
        ];

        return <BaseMoreAction actions={actions} />;
      },
    },
  ];
};

const Order = (props: Props) => {
  //#region Destructuring Props
  const {} = props;

  const { canViewOrders, canDetailOrders, canEditOrders, canDeleteOrders } = usePermissions(ORDER_PERMISSIONS);
  //#endregion Destructuring Props

  //#region Declare Hook
  const { t } = useTranslation(['admin/order', 'common/base']);
  const navigate = useNavigate();
  const dispatch = useAppDispatch();

  const [searchParams, setSearchParams] = useSearchParams();
  const typeList = searchParams.get('type');
  const params = useMemo(() => Object.fromEntries([...searchParams]), [searchParams]);
  const pageSelected = useMemo<number>(() => Number(params?.page ?? DEFAULT_CURRENT_PAGE), [params?.page]);
  const limitPage = useMemo<number>(() => Number(params?.limit ?? DEFAULT_LIMIT_PAGE), [params?.limit]);

  const currentRole = JSON.parse(localStorage.getItem(StorageEnum.ROLE)!)[0];
  //#endregion Declare Hook

  //#region Selector
  const isRefreshListOrder = useSelector(selectIsRefreshOrderList);
  //#endregion Selector

  //#region Declare State
  const [sortBy, setSortBy] = useState<ISortBy[]>(DEFAULT_SORT_ORDER);
  const [filterParams, setFilterParams] = useState<IParamQueryOrder>({
    ...(FILTER_DEFAULT_VALUES as IParamQueryOrder),
    ...(typeList === OrderListTypeEnum.NOT_FINALIZED && {
      status: [OrderStatusEnum.HOLD, OrderStatusEnum.PENDING, OrderStatusEnum.BOOKED],
    }),
    ...(typeList === OrderListTypeEnum.FINALIZED && {
      status: [OrderStatusEnum.FINALIZED],
    }),
    ...(typeList === OrderListTypeEnum.CUSTOMER_PICKUPS && {
      pickUp: OrderPickupEnum.CUSTOMER_PICKUPS,
    }),
  });
  const [searchKey, setSearchKey] = useState<string>(EMPTY_STRING);
  const debounceSearchKey = useDebounce(searchKey, DEFAULT_DEBOUNCE_DELAY_TIME);

  const [paramObject, setParamObject] = useState<IParamQueryOrder>({
    page: pageSelected,
    limit: limitPage,
    ...(typeList === OrderListTypeEnum.NOT_FINALIZED && {
      startDate: moment().subtract(28, 'days').format(DateFormatEnum.YYYY_MM_DD),
      status: [OrderStatusEnum.HOLD, OrderStatusEnum.PENDING, OrderStatusEnum.BOOKED],
    }),
    ...(typeList === OrderListTypeEnum.FINALIZED && {
      status: [OrderStatusEnum.FINALIZED],
    }),
    ...(typeList === OrderListTypeEnum.CUSTOMER_PICKUPS && {
      pickUp: OrderPickupEnum.CUSTOMER_PICKUPS,
    }),
  });
  const [pagination, setPagination] = useState<IPaginationResponse>();

  const [orderList, setOrderList] = useState<IOrder[]>([]);
  const [totalItems, setTotalItems] = useState<number>(0);

  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [isShowUnderDevelopment, setIsShowUnderDevelopment] = useState<boolean>(false);

  const [idsOrder, setIdsOrder] = useState<string[]>([]);
  const [selectedItems, setSelectedItems] = useState<IOrder[]>([]);
  const [idOrder, setIdOrder] = useState<string>(EMPTY_STRING);

  const [isShowConfirmDelete, setIsShowConfirmDelete] = useState<boolean>(false);
  //#endregion Declare State

  //#region Declare Memo
  //#endregion Declare Memo

  //#region Implement Hook
  useEffect(() => {
    handleGetListOrder(paramObject);

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [paramObject]);

  useEffect(() => {
    if (!isRefreshListOrder) return;

    handleGetListOrder(paramObject);

    dispatch(orderActions.setRefreshOrderList(false));

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isRefreshListOrder]);

  useEffect(() => {
    if (pagination || debounceSearchKey) {
      setParamObject({
        ...paramObject,
        page: DEFAULT_CURRENT_PAGE,
        limit: limitPage,
        ...(debounceSearchKey ? { textSearch: debounceSearchKey } : { textSearch: undefined }),
        ...(filterParams ? { ...filterParams } : {}),
      });

      setSearchParams({
        page: String(DEFAULT_CURRENT_PAGE),
        limit: String(limitPage),
        ...(searchKey ? { textSearch: debounceSearchKey } : {}),
      });
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debounceSearchKey]);
  //#endregion Implement Hook

  //#region Handle Function
  const handleGetListOrder = (params: IParamQueryOrder) => {
    setIsLoading(true);

    dispatch(getListOrder(params))
      .unwrap()
      .then((res) => {
        const { responses, pagination, totalItems } = res?.data;

        if (!responses) return;

        setOrderList(responses);
        setPagination(pagination);

        setTotalItems(totalItems);
      })
      .catch((_error) => {})
      .finally(() => {
        setIsLoading(false);
      });
  };

  const handleEdit = (id: string) => {
    switch (currentRole) {
      case RoleEnum.SUPER_ADMIN:
        navigate(adminRouteAbsolute.updateOrder(id));
        break;
      case RoleEnum.SALES_ADMIN:
        navigate(salesAdminRouteAbsolute.updateOrder(id));
        break;
      case RoleEnum.SALES_PERSON:
        navigate(salesPersonRouteAbsolute.updateOrder(id));
        break;
      case RoleEnum.OPERATIONS_MANAGER:
        navigate(operationsManagerRouteAbsolute.updateOrder(id));
        break;
      case RoleEnum.WAREHOUSE_LEAD:
        navigate(warehouseLeadRouteAbsolute.updateOrder(id));
        break;
      case RoleEnum.WAREHOUSE_STAFF:
        navigate(warehouseStaffRouteAbsolute.updateOrder(id));
        break;
      case RoleEnum.FOREMAN:
        navigate(foremanRouteAbsolute.updateOrder(id));
        break;
      case RoleEnum.CREW:
        navigate(crewRouteAbsolute.updateOrder(id));
        break;
      case RoleEnum.LOADER:
        navigate(loaderRouteAbsolute.updateOrder(id));
        break;
    }
  };

  const handleView = (id: string, item: IOrder) => {
    switch (currentRole) {
      case RoleEnum.SUPER_ADMIN:
        navigate(`${adminRouteAbsolute.detailOrder(id)}?orderNumber=${item?.orderNumber}`);
        break;
      case RoleEnum.SALES_ADMIN:
        navigate(`${salesAdminRouteAbsolute.detailOrder(id)}?orderNumber=${item?.orderNumber}`);
        break;
      case RoleEnum.SALES_PERSON:
        navigate(`${salesPersonRouteAbsolute.detailOrder(id)}?orderNumber=${item?.orderNumber}`);
        break;
      case RoleEnum.OPERATIONS_MANAGER:
        navigate(`${operationsManagerRouteAbsolute.detailOrder(id)}?orderNumber=${item?.orderNumber}`);
        break;
      case RoleEnum.WAREHOUSE_LEAD:
        navigate(`${warehouseLeadRouteAbsolute.detailOrder(id)}?orderNumber=${item?.orderNumber}`);
        break;
      case RoleEnum.WAREHOUSE_STAFF:
        navigate(`${warehouseStaffRouteAbsolute.detailOrder(id)}?orderNumber=${item?.orderNumber}`);
        break;
      case RoleEnum.FOREMAN:
        navigate(`${foremanRouteAbsolute.detailOrder(id)}?orderNumber=${item?.orderNumber}`);
        break;
      case RoleEnum.CREW:
        navigate(`${crewRouteAbsolute.detailOrder(id)}?orderNumber=${item?.orderNumber}`);
        break;
      case RoleEnum.LOADER:
        navigate(`${loaderRouteAbsolute.detailOrder(id)}?orderNumber=${item?.orderNumber}`);
        break;
    }
  };

  const handleDelete = (id: string) => {
    if (!id) return;

    setIdOrder(id);
    setIsShowConfirmDelete(true);
  };

  const handleCloseConfirmDelete = () => {
    setIdOrder(EMPTY_STRING);
    setIsShowConfirmDelete(false);
  };

  const handleConfirmDelete = () => {
    setIsLoading(true);

    dispatch(deleteOrder(idOrder))
      .unwrap()
      .then(() => {
        setIdOrder(EMPTY_STRING);
        handleCloseConfirmDelete();
        dispatch(orderActions.setRefreshOrderList(true));
      })
      .catch(() => {})
      .finally(() => {
        setIsLoading(false);
      });
  };

  const handleRowsSelect = (ids: string[], selectedItems?: IOrder[]) => {
    setIdsOrder(ids);
    setSelectedItems(selectedItems || []);
  };

  const handleSearchChange = (value: string) => {
    setSearchKey(value);
  };

  const handleSort = (sortType: string, name: string) => {
    const updatedSortBy = sortBy.map((item) => ({
      ...item,
      typeSort: item.name === name ? sortType : undefined,
    }));
    setSortBy(updatedSortBy);
    setParamObject({ ...paramObject, sortBy: name, sortType: sortType });
    setSearchParams({ ...params, sortBy: name, sortType: sortType });
  };

  const fetchOptionFilter = useCallback(async (): Promise<IResultCallApiFn<IBaseOption>> => {
    const result: IResultCallApiFn<IBaseOption> = {
      optionsResp: optionStatusOrder,
      hasMoreResp: false,
    };

    return result;
  }, []);

  const handleApplyFilter = (values: IParamQueryOrder | undefined) => {
    if (!values) {
      setFilterParams({});
      return;
    }

    setFilterParams({
      ...values,
    });

    if (values) {
      setParamObject({
        ...paramObject,
        status: values?.status,
        salesAccountId: values?.salesAccountId,
        clientId: values?.clientId,
        startDate: values?.startDate,
        endDate: values?.endDate,
      });
    }
  };

  const handleClearFilter = () => {
    setFilterParams(FILTER_DEFAULT_VALUES as IParamQueryOrder);
    setParamObject({
      ...paramObject,
      page: DEFAULT_CURRENT_PAGE,
      limit: limitPage,
      status: undefined,
      salesAccountId: undefined,
      clientId: undefined,
      startDate: undefined,
      endDate: undefined,
    });
  };

  const handleFilterStatusChange = (
    status: string[],
    onChange?: (value: IFilterValueChange<IParamQueryOrder>) => void
  ) => {
    if (!status) return;

    onChange?.({ name: 'status', value: status });
    setFilterParams({ ...filterParams, status: status });
  };

  const handlePageChange = (_event: ChangeEvent<unknown>, page: number) => {
    setParamObject({ ...paramObject, page });
    setSearchParams({ ...params, page: String(page), limit: String(limitPage) });
  };

  const handleShowUnderDevelopment = () => {
    setIsShowUnderDevelopment(!isShowUnderDevelopment);
  };

  const handleFilterSalesPersonChange = (
    data: IBaseOption,
    onChange?: (value: IFilterValueChange<IParamQueryOrder>) => void
  ) => {
    if (!data) return;

    const { value } = data;

    onChange?.({ name: 'salesAccountId', value: value });
    setFilterParams({ ...filterParams, salesAccountId: value });
  };

  const handleFilterClientChange = (
    salesAccountId: IBaseOption,
    onChange?: (value: IFilterValueChange<IParamQueryOrder>) => void
  ) => {
    if (!salesAccountId) return;

    const { value } = salesAccountId;

    onChange?.({ name: 'clientId', value: value });
    setFilterParams({ ...filterParams, clientId: value });
  };

  const handleChangeDate = (date: string | { startDate: string; endDate: string }) => {
    if (typeof date !== 'string') {
      const { startDate, endDate } = date;
      setFilterParams((prev) => ({
        ...prev,
        startDate,
        endDate,
      }));
    }
  };

  const fetchOptionSalePerson = useCallback(
    async (payload: IPayloadCallApiFn): Promise<IResultCallApiFn<IBaseOption>> => {
      const { page, searchKey } = payload;

      const payloadParams: IQueryParamUser = {
        limit: DEFAULT_LIMIT_PAGE,
        page: page,
        role: RoleEnum.SALES_PERSON,
        ...(searchKey && { textSearch: searchKey }),
      };

      try {
        const resp = await dispatch(getListUser(payloadParams)).unwrap();

        const { responses, pagination } = resp?.data;

        const formatOptions: IBaseOption[] = responses.map((item) => ({
          value: item?.id,
          label: getFullName({ firstName: item?.firstName, lastName: item?.lastName }),
        }));

        const hasMoreResp: boolean = pagination.page < pagination.totalPages || false;

        return {
          optionsResp: formatOptions,
          hasMoreResp: hasMoreResp,
        };
      } catch (_error) {
        return DEFAULT_SELECT_DYNAMIC_RESULT_FETCH_DATA;
      }
    },
    []
  );

  const fetchOptionsClient = useCallback(
    async (payload: IPayloadCallApiFn): Promise<IResultCallApiFn<IOptionClient>> => {
      const { searchKey, page } = payload;
      const payloadParams: IQueryParamClient = {
        page: page,
        limit: DEFAULT_LIMIT_PAGE,
        type: TypeCrmEnum.CLIENT,
        ...(searchKey && { textSearch: searchKey }),
      };

      try {
        const resp = await dispatch(getListClient(payloadParams)).unwrap();

        const { responses: listClient, pagination } = resp?.data;

        const formatOptions: IOptionClient[] = listClient?.map((item: IClient) => ({
          value: item?.id,
          label:
            item?.firstName || item?.lastName
              ? getFullName({ firstName: item?.firstName, lastName: item?.lastName })
              : item?.entityName,
          email: item?.clientContacts?.[0]?.email || EMPTY_STRING,
        }));

        const hasMore: boolean = pagination?.page < pagination?.totalPages || false;

        return { optionsResp: formatOptions, hasMoreResp: hasMore };
      } catch (error) {
        return DEFAULT_SELECT_DYNAMIC_RESULT_FETCH_DATA;
      }
    },
    []
  );
  //#endregion Handle Function

  return (
    <Box id='orderPage' className={cx('container')}>
      <BaseToolbar
        title={t('order_title_order_management')}
        total={t('order_title_total_orders', { total: totalItems })}
      >
        <BaseInputSearch
          width={200}
          typeStyle='outlined'
          onChange={handleSearchChange}
          borderRadius={100}
          placeholder={t('order_title_search')}
        />

        <BaseFilters
          value={filterParams}
          defaultValue={FILTER_DEFAULT_VALUES as IParamQueryOrder}
          onApply={handleApplyFilter}
          onClear={handleClearFilter}
        >
          {({ value, onChange }) => (
            <Stack direction={'column'} width={340} mb={4} gap={6}>
              <BaseDynamicSelect
                multiple={true}
                gap={10}
                label={t('order_title_filter_status')}
                placeholder={t('order_title_filter_all_status')}
                callApiFn={fetchOptionFilter}
                value={value?.status}
                onChange={(selected) =>
                  handleFilterStatusChange(
                    selected.optionsSelected.map((item) => item?.value),
                    onChange
                  )
                }
              />

              <BaseSelect
                isSearch
                gap={10}
                label={t('order_title_filter_client')}
                callApiFn={fetchOptionsClient}
                type='loadMore'
                value={value?.clientId}
                onChange={(option) => handleFilterClientChange(option as any, onChange)}
              />

              <BaseSelect
                isSearch
                gap={10}
                label={t('order_title_filter_sale_person')}
                callApiFn={fetchOptionSalePerson}
                type='loadMore'
                value={value?.salesAccountId}
                onChange={(option) => handleFilterSalesPersonChange(option as any, onChange)}
              />

              <BaseDatePicker
                mode='rangeDate'
                label={t('order_title_filter_date')}
                value={{
                  startDate: filterParams?.startDate || EMPTY_STRING,
                  endDate: filterParams?.endDate || EMPTY_STRING,
                }}
                onChange={handleChangeDate}
              />
            </Stack>
          )}
        </BaseFilters>
      </BaseToolbar>

      <Box component={'div'} className={cx('body')}>
        <BaseTable
          dataSource={canViewOrders ? orderList : []}
          currentPage={pageSelected}
          totalPage={pagination?.totalPages}
          columns={columnsOrder(
            t,
            sortBy,
            handleSort,
            handleView,
            handleEdit,
            handleDelete,
            canDetailOrders,
            canEditOrders,
            canDeleteOrders
          )}
          onPageChange={handlePageChange}
          // dataSelectedIds={idsOrder}
          // onSelectChange={handleRowsSelect}
          {...(canDetailOrders ? { onRowClick: handleView } : {})}
        />
      </Box>
      {isShowConfirmDelete && (
        <ConfirmModal
          type='delete'
          icon={icons.tableIconActionDelete}
          title={t('common_title_confirm_delete', { ns: 'common/base' })}
          textConfirm={t('common_action_delete', { ns: 'common/base' })}
          onClose={handleCloseConfirmDelete}
          onConfirm={handleConfirmDelete}
        />
      )}

      {isLoading && <Loading />}

      {isShowUnderDevelopment && <UnderDevelopment onClose={handleShowUnderDevelopment} />}
    </Box>
  );
};

export default Order;
